<script>
import axios from 'axios';
import { createAssortmentApp } from '@/create-app';
import { useContextStore } from '@/stores/context';
import { setEmbedded, STYLE_SCOPE_CLASS } from '@/webshell-embed/embedded';
import { shellBridge } from '@/webshell-embed/platform-bridge';

// The remote entry the One Retail shell loads over Module Federation (`./webshell-main`). Mounts
// the whole Assortment app — router, stores, Vuetify, i18n, axios — into a shell-provided node,
// sharing the Vue/Vuetify singletons. The shell owns the chrome, so AO runs embedded with its navbar and
// footer hidden.
//
// Everything that only makes sense embedded is settled here rather than behind an `if (embedded)` in
// create-app.js: this component only ever runs inside the shell, so the condition is the file.
export default {
  name: 'AssortmentRemote',

  computed: {
    scopeClass: () => STYLE_SCOPE_CLASS,
  },

  async mounted() {
    // Before create-app.js reads it — the router, the axios interceptors and App.vue all branch on it.
    setEmbedded(true);

    // The shell URL is `/{tenant}/{app}/...` - ADR-004 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/004-tenant-in-url-path.md
    // AO shares the shell's window, so read the tenant straight off the path.
    const [tenant, app] = window.location.pathname.split('/').filter(Boolean);
    if (!tenant || !app) {
      // Thrown rather than mounted under `/undefined/undefined`
      // The shell will show the error during the bootstrapping/startup process.
      throw new Error(`Assortment mounted outside /{tenant}/{app}: ${window.location.pathname}`);
    }
    const routerBase = `/${tenant}/${app}`;
    // Everything past the base is AO's own route. A memory history can't read it off the URL, so
    // hand it over.
    const { pathname, search, hash } = window.location;
    const initialPath = `${pathname.slice(routerBase.length) || '/'}${search}${hash}`;

    // AO is DOM-mounted into the shell's document, so a relative '/api/...' would resolve against the
    // shell's origin. Point axios at the origin this federated chunk was served from and call AO's
    // backend cross-origin (CORS + credentials are set in plugins/axios.js and server/config/cors.js).
    // Derived at runtime so nothing is baked into the build.
    axios.defaults.baseURL = new URL(import.meta.url).origin;

    // Seed the tenant before the first API request so AO's axios interceptor stamps `x-rtls-tenant`
    // on every call — including the first `/api/user-context` — rather than defaulting to AO's first
    // tenant. Assigned directly because `setCurrentTenant`'s guard drops it while `availableTenants`
    // is still empty; the user-context load validates it right after. Bound to the shell's pinia
    // explicitly, which is the one create-app.js reuses embedded.
    useContextStore(shellBridge()?.pinia).currentTenant = tenant;

    const { dispose, whenMounted } = createAssortmentApp({
      mountTarget: this.$refs.host,
      // The router's hrefs carry the shell path, so links open the shell; API calls go cross-origin
      // to AO's own backend.
      routerBase,
      initialPath,
    });
    this.disposeApp = dispose;

    // A failed startup rejects here, and the shell's error boundary shows its unavailable state.
    await whenMounted;
  },

  beforeUnmount() {
    // Withdraw everything AO put into the shell, and stop it mounting if it has not yet: the shell's
    // stores outlive this component, so anything left behind follows the user into the next product.
    this.disposeApp?.();
    this.disposeApp = null;
  },
};
</script>

<template>
  <div ref="host" :class="scopeClass" />
</template>
